
import { Entity } from '../entity';
import { JobCreateRequest } from '../../requests/job/job.create.request';
import { PriorityTiny } from '../job/priority.tiny';
import { JobTemplate } from './job.template'
import { JobTemplateJob } from './job.template.job';
import { JobTemplateQuestionAnswer, JobTemplateQuestionOption, JobTemplateQuestionType, JobTemplateQuestionTypeAllowedActions } from './job.template.questions';
import { Activity } from '../shared/activity';
import { ActivityEntityName } from '../shared/activity.entity.name';
import * as Enumerable from 'linq';
import { DateUtility } from '../../utilities/date.utility';
import { Opportunity } from '../opportunities/opportunity';
import { QuotePro, QuoteSimpleStatus } from '../quotes/quote.pro';
import { v4 } from 'uuid';
import { PricingMethod } from '../price.book';


interface JobTemplateQuestionOptionWithValue extends JobTemplateQuestionOption {
    value?: any;
}

export class DynamicJobTemplateDomain {

    private jobTemplate: JobTemplateJob;
    private questionAnswers: JobTemplateQuestionAnswer[] = [];

    private request: Partial<JobCreateRequest>;
    private activity: Partial<Activity>;

    private constructor(
        private template: JobTemplate,
        private timezone: string = 'utc',
        private dateFormat: string = 'dd/MM/yyyy',
    ) {
        this.jobTemplate = template?.template as JobTemplateJob;
    }

    public static from(template: JobTemplate, timezone = 'utc', dateFormat = 'dd/MM/yyyy'): DynamicJobTemplateDomain {

        if (template?.templateMode === 'Static') {
            throw new Error('Cannot create a dynamic job template domain from a static job template');
        }

        return new DynamicJobTemplateDomain(template, timezone);
    }

    public addAnswer(answer: JobTemplateQuestionAnswer): void {
        this.questionAnswers.push(answer);
        this.build();
    }

    public toJobCreateRequest(): Partial<JobCreateRequest> {
        console.log('Pricelist =>', this.request?.priceBook?.name)
        return this.request;
    }

    private buildCreateRequest(): Partial<JobCreateRequest> {
        const request: Partial<JobCreateRequest> = {
            mode: this?.template?.mode,
            description: null,
            type: this?.jobTemplate?.type,
            priority: this?.jobTemplate?.priority as PriorityTiny,
            owner: this?.jobTemplate?.owner,
            fieldSupervisor: this?.jobTemplate?.fieldSupervisor,
            priceBook: this?.jobTemplate?.priceBook as Entity,
            labels: this?.jobTemplate?.labels as any ?? [],
            codes: [],
            customFields: this.jobTemplate?.customFields ?? {},
            duration: this?.jobTemplate?.duration ?? 0,
        }

        if (this?.jobTemplate?.opportunityStage) {
            request.opportunityStage = this?.jobTemplate?.opportunityStage;
        }

        if (this?.jobTemplate?.opportunitySource) {
            request.opportunitySource = this?.jobTemplate?.opportunitySource;
        }

        return request;
    }

    private build() {

        let request = this.buildCreateRequest();

        const jobDescription = this.jobTemplate?.description?.trim()
            ? [this?.jobTemplate?.description?.trim(), '\n\n']
            : [];

        Enumerable
            .from(this.questionAnswers)
            .forEach(answer => {

                const question = answer.question;
                const questionType = answer.question.type;
                const answerValue = answer.value;

                this.isConditionMet(answer);

                if (answer.passedCondition === false) {
                    if (question.multiple) {
                        answer.value = [];

                        if (questionType === JobTemplateQuestionType.Numeric) {
                            Enumerable
                                .from(question.options)
                                .forEach((o, i) => {
                                    answer.value[i] = 0;
                                });
                        }
                    }
                    else {
                        if (questionType === JobTemplateQuestionType.Numeric) {
                            answer.value = 0;
                        }
                        else {
                            answer.value = null;
                        }
                    }
                    return
                }


                const allowedActions = JobTemplateQuestionTypeAllowedActions[questionType];

                let safeValues: JobTemplateQuestionOptionWithValue[] = [];

                switch (questionType) {

                    case JobTemplateQuestionType.Date: {
                        const defaultOption = Enumerable.from(question.options).firstOrDefault();
                        safeValues.push({ ...defaultOption, value: DateUtility.getDate(answerValue) });
                        break;
                    }

                    case JobTemplateQuestionType.Numeric: {
                        if (question.multiple) {

                            let safeNumberValues = answerValue ?? [];
                            Enumerable
                                .from(question.options)
                                .forEach((o, i) => {
                                    const value = safeNumberValues[i] ?? 0;
                                    if (value)
                                        safeValues.push({ ...o, value });
                                    //safeValues.push({ ...o, value: answerValue[i] });
                                });

                        } else {
                            const defaultOption = Enumerable.from(question.options).firstOrDefault();
                            safeValues.push({ ...defaultOption, value: answerValue });
                        }
                        break;
                    }

                    case JobTemplateQuestionType.Text: {
                        const defaultOption = Enumerable.from(question.options).firstOrDefault();
                        safeValues.push({ ...defaultOption, value: answerValue });
                        break;
                    }

                    case JobTemplateQuestionType.Boolean: {
                        const safeValue = answerValue ?? Enumerable.from(question.options).lastOrDefault();
                        if (safeValue) safeValues.push(safeValue);
                        break;
                    }

                    case JobTemplateQuestionType.Dropdown: {

                        if (!question.multiple && answerValue) {
                            safeValues.push(answerValue)
                            break;
                        }

                        let arrayAnswerValue = answerValue ?? [];
                        safeValues.push(...arrayAnswerValue)
                        break;
                    }
                }


                let questionJobDescription = [question?.name?.trim(), '\n'];

                Enumerable
                    .from(safeValues)
                    .forEach(option => {

                        const optionName = option?.name?.trim();
                        let optionValue = option?.value ?? null

                        switch (questionType) {
                            case JobTemplateQuestionType.Date: {
                                optionValue = DateUtility.formatDateTime(optionValue as any, this.timezone, this.dateFormat);
                                break;
                            }
                        }

                        let optionAnswer = null;
                        if (option?.value) {
                            if (safeValues?.length === 1) {
                                optionAnswer = ['- ', optionValue].join('')
                            }
                            else {
                                optionAnswer = ['- ', optionName, '\n', optionValue].join('');
                            }
                        }
                        else {
                            optionAnswer = ['- ', optionName].join('');
                        }

                        if (optionAnswer) {
                            questionJobDescription.push(optionAnswer + '\n');
                        }

                        if (allowedActions.appendToJobDescription && option?.appendToJobDescription) {
                            if (option?.jobDescription) {
                                questionJobDescription.push(option?.jobDescription + '\n');
                            }
                        }

                        questionJobDescription.push('\n');

                        if (allowedActions.setJobType && option?.jobType) {
                            request.type = option.jobType;
                        }

                        if (allowedActions.setPriority && option?.priority) {
                            request.priority = option?.priority as PriorityTiny;
                        }

                        if (allowedActions.setPriceList && option?.priceList) {
                            request.priceBook = option?.priceList as PriorityTiny;
                        }

                        if (allowedActions.setOwner && option?.owner) {
                            request.owner = option?.owner as PriorityTiny;
                        }

                        if (option?.fieldSupervisor) {
                            request.fieldSupervisor = option?.fieldSupervisor as PriorityTiny;
                        }

                        if (allowedActions.setTag && option?.tags) {
                            const distinctTags = Enumerable
                                .from([...request?.labels as Entity[], ...option.tags as Entity[]])
                                .distinct(x => x._id)
                                .toArray();
                            request.labels = distinctTags;
                        }

                        if (allowedActions.setCustomFields && option?.customFields) {

                            const customFields = option.customFields;

                            Object
                                .keys(customFields)
                                .forEach(key => {
                                    const value = customFields[key];
                                    request.customFields[key] = value;
                                });

                        }

                        if (allowedActions.setOpportunityStage && option?.opportunityStage) {
                            request.opportunityStage = option?.opportunityStage;
                        }

                        if (allowedActions.setOpportunitySource && option?.opportunitySource) {
                            request.opportunitySource = option?.opportunitySource;
                        }

                        if (allowedActions.setOpportunityEstimatedAmount && option?.opportunityEstimatedAmount) {
                            request.opportunityAmount = option?.opportunityEstimatedAmount;
                        }

                        if (allowedActions.setOpportunityExpectedCloseDate && option?.opportunityExpectedCloseDate) {
                            request.opportunityExpectedClose = option?.opportunityExpectedCloseDate;
                        }

                        if (allowedActions.addItems && option?.items?.length) {

                            const processNumeric = (value: number | number[]) => {

                                const saveValues = Array.isArray(value) ? value : [value];

                                const allItems = Enumerable
                                    .from(saveValues)
                                    .select(safeValue => {

                                        const items = Enumerable
                                            .from(option.items)
                                            .where(_ => safeValue > 0)
                                            .select(code => {

                                                const safeQuantity = ((safeValue ?? 1) * (code?.quantity ?? 1));

                                                return {
                                                    ...code,
                                                    quantity: safeQuantity ? safeQuantity : 1
                                                }
                                            })
                                            .toArray();

                                        return items;
                                    })
                                    .selectMany(x => x)
                                    .toArray();

                                return allItems;
                            }

                            const answerCodes = questionType === JobTemplateQuestionType.Numeric
                                ? processNumeric(option?.value)
                                : option.items;

                            request.codes = Enumerable
                                .from(request.codes as any[])
                                .concat(answerCodes)
                                .toArray();
                        }

                        if (option?.addToDuration) {
                            const optionValue = option?.value ?? 1;
                            const safeValue = Number.isNaN(optionValue)
                                ? 1
                                : optionValue;

                            if (safeValue && !Number.isNaN(safeValue)) {
                                request.duration += (option.addToDuration * safeValue);
                            }
                        }

                        if (option?.setCustomField) {
                            request.customFields[option.setCustomField] = option.value;
                        }

                    });

                if (questionJobDescription?.length) {
                    jobDescription.push(...questionJobDescription.join(''));
                }

            });

        request.description = jobDescription.join('').trim();

        this.request = request;

    }

    public isConditionMet(qa: JobTemplateQuestionAnswer): boolean {
        return DynamicJobTemplateDomain.isConditionMet(this.questionAnswers, qa);
    }

    public static isConditionMet(questionAnswers: JobTemplateQuestionAnswer[], questionAnswer: JobTemplateQuestionAnswer): boolean {

        const { question, value } = questionAnswer;
        questionAnswer.passedCondition = true;

        if (!question?.showCondition) return true;

        const { question: conditionQuestionId, value: conditionValueId } = question?.showCondition;

        const conditionAnswer = Enumerable
            .from(questionAnswers)
            .where(x => x.question._id !== question._id)
            .firstOrDefault(x => x.question._id === conditionQuestionId);

        if (!conditionAnswer) {
            return false;
        }

        const conditionArray = Array.isArray(conditionAnswer?.value ?? [])
            ? conditionAnswer?.value
            : [conditionAnswer?.value];

        const passed = Enumerable
            .from(conditionArray)
            .any(x => x['_id'] === conditionValueId);

        questionAnswer.passedCondition = passed;
        return passed;

    }
}

interface OpportunityWithQuote {
    opportunity: Partial<Opportunity>;
    quote: Partial<QuotePro>;
}

export function getOpportunityWithQuoteFromJobCreateRequest(
    request: Partial<JobCreateRequest>,
    quoteTemplate: QuotePro,
    jobTemplate: JobTemplate
): OpportunityWithQuote {

    const opportunity = {
        _id: v4(),

        opportunityNumber: null,
        name: jobTemplate.name,
        description: request.description ?? jobTemplate?.template?.description,
        amount: request.opportunityAmount ?? 0,

        account: request.customer,
        contact: request.contact,

        owner: request.owner ?? jobTemplate?.template?.owner,
        // priceBook: request.priceBook,
        jobType: request.type ?? jobTemplate?.template?.type,
        tags: request.labels ?? jobTemplate?.template?.labels,
        customFields: request.customFields ?? jobTemplate?.template?.customFields,

        site: request.site,
        template: {
            _id: jobTemplate._id,
            name: jobTemplate.name
        },

        stage: request.opportunityStage ?? jobTemplate?.template?.opportunityStage,
        source: request.opportunitySource ?? jobTemplate?.template?.opportunitySource,
        expectedClose: request.opportunityExpectedClose,
    } as Partial<Opportunity>;

    // if (!opportunity.stage) {
    //     delete opportunity.stage;
    // }

    // if (!opportunity.source) {
    //     delete opportunity.source;
    // }

    // if (!opportunity.expectedClose) {
    //     delete opportunity.expectedClose;
    // }

    if (!opportunity.account)
        delete opportunity.account;

    if (!opportunity.contact)
        delete opportunity.contact;

    if (!opportunity.site)
        delete opportunity.site;

    const quote = {
        ...quoteTemplate,
        _id: null,
        status: QuoteSimpleStatus.Draft,
        name: jobTemplate.name,
        date: new Date(),
        owner: request.owner,
        sections: [
            ...(request?.codes?.length > 0 ? [{
                _id: v4(),
                name: '<Automatically added by opportunity template>',
                items: request?.codes ?? [],
                costOnly: false,
            }] : []),
            ...(quoteTemplate?.sections ?? [])
        ],
        links: [
            {
                _id: opportunity._id,
                type: ActivityEntityName.Opportunity
            }
        ],

        priceList: request.priceBook,
        pricingMethod: PricingMethod.PriceList

    } as Partial<QuotePro>;

    return {
        opportunity,
        quote
    }

}